import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  createContext,
} from 'react';
import {
  clamp,
  clampZoom,
  computeFitZoom,
  computeVisibleRange,
  pxToSec,
  secToPx,
  zoomIn as zoomInLevel,
  zoomOut as zoomOutLevel,
  zoomToPxPerSec,
} from '@src/services/timeline';
import {
  APPEND_GAP_PX,
  CONTENT_END_PAD_PX,
  DEFAULT_ZOOM,
  START_OFFSET,
} from '@src/constants/timeline';

/**
 * Single source of truth for the timeline's scale + scroll.
 * Consumed via the useTimeline hook. See TimelineProvider for the shape.
 */
export const TimelineContext = createContext(null);

/**
 * Holds the timeline scale (zoom → pxPerSec) and scroll position.
 *
 * Scroll is deliberately NOT React state: it lives in a ref and is pushed to
 * subscribers every frame, so scrolling never re-renders the tree. Only
 * `visibleRange` is state, and it changes at most once per whole second of
 * scroll — the coarse bucket lanes use to window their clips.
 */
const TimelineProvider = ({
  durationSec,
  initialZoom = DEFAULT_ZOOM,
  children = null,
}) => {
  const [zoomLevel, setZoomLevelState] = useState(clampZoom(initialZoom));
  const [viewportWidth, setViewportWidth] = useState(0);
  const [visibleRange, setVisibleRange] = useState({
    fromSec: 0,
    toSec: durationSec,
  });

  const scrollRef = useRef(0);
  const viewportRef = useRef(null);
  const listenersRef = useRef(new Set());

  const pxPerSec = zoomToPxPerSec(zoomLevel);
  // Let `fit` read the current scale without depending on it — so it keeps a
  // stable identity and the auto-fit effect doesn't re-run on every zoom step.
  const pxPerSecRef = useRef(pxPerSec);
  useEffect(() => {
    pxPerSecRef.current = pxPerSec;
  }, [pxPerSec]);

  const recomputeVisibleRange = useCallback(
    (scrollLeft, width, pps) => {
      const next = computeVisibleRange({
        scrollLeft,
        viewportWidth: width,
        pxPerSec: pps,
        durationSec,
      });
      // Bucket to whole seconds so fast scrolling doesn't churn React state.
      setVisibleRange((prev) => {
        const from = Math.floor(next.fromSec);
        const to = Math.ceil(next.toSec);
        if (from === prev.fromSec && to === prev.toSec) return prev;
        return { fromSec: from, toSec: to };
      });
    },
    [durationSec],
  );

  const setScrollLeft = useCallback(
    (scrollLeft) => {
      scrollRef.current = scrollLeft;
      listenersRef.current.forEach((cb) => cb(scrollLeft));
      recomputeVisibleRange(scrollLeft, viewportWidth, pxPerSec);
    },
    [recomputeVisibleRange, viewportWidth, pxPerSec],
  );

  const subscribeScroll = useCallback((cb) => {
    listenersRef.current.add(cb);
    return () => listenersRef.current.delete(cb);
  }, []);

  // Imperatively nudge the native viewport scroll (used by drag-seek auto-scroll
  // at the edges). We move the DOM, mirror the position into scrollRef, AND
  // notify subscribers synchronously — NOT via the throttled native onScroll,
  // which lands a frame or two later. Notifying now moves the overlay chip's
  // scroll-shift in the same frame as the natively-scrolled content, and the
  // fresh scrollRef keeps the chip correct on the next re-render's layout effect
  // — so nothing lags behind during auto-scroll.
  const scrollViewportBy = useCallback((deltaPx) => {
    const el = viewportRef.current;
    if (!el || !deltaPx) return;
    const max = Math.max(0, el.scrollWidth - el.clientWidth);
    const next = clamp(el.scrollLeft + deltaPx, 0, max);
    if (next === el.scrollLeft) return;
    el.scrollLeft = next;
    scrollRef.current = next;
    listenersRef.current.forEach((cb) => cb(next));
  }, []);

  // Recompute the visible window whenever the scale or viewport changes — not
  // only on scroll. Without this, a clip that extends the duration (e.g. a
  // newly appended block) would fall outside the stale window and never render
  // until the user scrolls.
  useEffect(() => {
    recomputeVisibleRange(scrollRef.current, viewportWidth, pxPerSec);
  }, [recomputeVisibleRange, viewportWidth, pxPerSec]);

  const setZoomLevel = useCallback((next) => {
    setZoomLevelState((prev) =>
      clampZoom(typeof next === 'function' ? next(prev) : next),
    );
  }, []);

  const zoomInAction = useCallback(() => setZoomLevelState(zoomInLevel), []);
  const zoomOutAction = useCallback(() => setZoomLevelState(zoomOutLevel), []);

  const fit = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    // Measure the append buttons ("Thêm khối" / "Thêm nhạc nền") in content px:
    // they overflow past the last clip, so the fit must leave room for them.
    const viewLeft = el.getBoundingClientRect().left;
    const appendBoxes = [...el.querySelectorAll('[data-timeline-append]')].map(
      (node) => {
        const rect = node.getBoundingClientRect();
        return {
          leftPx: rect.left - viewLeft + el.scrollLeft,
          widthPx: rect.width,
        };
      },
    );
    setZoomLevelState(
      computeFitZoom({
        pxPerSec: pxPerSecRef.current,
        viewportWidth,
        durationSec,
        appendBoxes,
        offsetPx: START_OFFSET,
        gapPx: APPEND_GAP_PX,
        endPadPx: CONTENT_END_PAD_PX,
      }),
    );
  }, [durationSec, viewportWidth]);

  const value = useMemo(
    () => ({
      durationSec,
      pxPerSec,
      zoomLevel,
      setZoomLevel,
      zoomIn: zoomInAction,
      zoomOut: zoomOutAction,
      fit,
      viewportWidth,
      setViewportWidth,
      scrollRef,
      viewportRef,
      setScrollLeft,
      scrollViewportBy,
      subscribeScroll,
      visibleRange,
      secToPx: (sec) => secToPx(sec, pxPerSec) + START_OFFSET,
      pxToSec: (px) => pxToSec(px - START_OFFSET, pxPerSec),
    }),
    [
      durationSec,
      pxPerSec,
      zoomLevel,
      setZoomLevel,
      zoomInAction,
      zoomOutAction,
      fit,
      viewportWidth,
      setScrollLeft,
      scrollViewportBy,
      subscribeScroll,
      visibleRange,
    ],
  );

  return (
    <TimelineContext.Provider value={value}>
      {children}
    </TimelineContext.Provider>
  );
};

export default TimelineProvider;
